import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { Upload, Pen, Calendar } from 'lucide-react';
import { Button } from '../ui/Button';
export function DynamicFormGenerator({ fields, currentUserRole, values, signatures, onChange, onSignature, onImageUpload, readOnly = false }) {
    const [showSignatureModal, setShowSignatureModal] = useState(null);
    // Filter fields based on user role
    const visibleFields = fields
        .filter(field => field.visibleTo.includes(currentUserRole) || field.visibleTo.includes('any'))
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    const canFillField = (field) => {
        if (readOnly)
            return false;
        return field.fillableBy.includes(currentUserRole) || field.fillableBy.includes('any');
    };
    // Group fields
    const groupedFields = {};
    visibleFields.forEach(field => {
        const group = field.group || 'general';
        if (!groupedFields[group])
            groupedFields[group] = [];
        groupedFields[group].push(field);
    });
    const renderField = (field) => {
        const value = values[field.variableName] || field.defaultValue || '';
        const isDisabled = !canFillField(field);
        const isRequired = field.validation?.required;
        const baseInputClass = "w-full px-4 py-2 rounded-lg";
        const inputStyle = {
            backgroundColor: isDisabled ? 'var(--bg-base)' : 'var(--bg-elevated)',
            border: '1px solid var(--border-color)',
            color: 'var(--text-primary)',
            opacity: isDisabled ? 0.6 : 1
        };
        switch (field.fieldType) {
            case 'text':
            case 'email':
            case 'phone':
                return (_jsx("input", { type: field.fieldType === 'email' ? 'email' : field.fieldType === 'phone' ? 'tel' : 'text', value: value, onChange: (e) => onChange(field.variableName, e.target.value), disabled: isDisabled, placeholder: field.placeholder, required: isRequired, className: baseInputClass, style: inputStyle }));
            case 'number':
            case 'currency':
                return (_jsx("input", { type: "number", value: value, onChange: (e) => onChange(field.variableName, e.target.value), disabled: isDisabled, placeholder: field.placeholder, required: isRequired, min: field.validation?.min, max: field.validation?.max, className: baseInputClass, style: inputStyle }));
            case 'date':
                return (_jsxs("div", { className: "relative", children: [_jsx("input", { type: "date", value: value, onChange: (e) => onChange(field.variableName, e.target.value), disabled: isDisabled, required: isRequired, className: baseInputClass, style: inputStyle }), _jsx(Calendar, { className: "absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none", style: { color: 'var(--text-tertiary)' } })] }));
            case 'address':
                return (_jsx("textarea", { value: value, onChange: (e) => onChange(field.variableName, e.target.value), disabled: isDisabled, placeholder: field.placeholder, required: isRequired, rows: 3, className: baseInputClass, style: inputStyle }));
            case 'dropdown':
                return (_jsxs("select", { value: value, onChange: (e) => onChange(field.variableName, e.target.value), disabled: isDisabled, required: isRequired, className: baseInputClass, style: inputStyle, children: [_jsx("option", { value: "", children: "Select..." }), field.options?.map((option) => (_jsx("option", { value: option.value, children: option.label }, option.value)))] }));
            case 'checkbox':
                return (_jsxs("label", { className: "flex items-center gap-2", children: [_jsx("input", { type: "checkbox", checked: !!value, onChange: (e) => onChange(field.variableName, e.target.checked), disabled: isDisabled, required: isRequired }), _jsx("span", { style: { color: 'var(--text-primary)' }, children: field.label })] }));
            case 'signature':
                return (_jsx("div", { className: "space-y-2", children: signatures[field.variableName] ? (_jsxs("div", { className: "border rounded-lg p-4", style: { borderColor: 'var(--border-color)' }, children: [_jsx("img", { src: signatures[field.variableName].data, alt: "Signature", className: "max-h-20" }), _jsxs("div", { className: "mt-2 text-xs", style: { color: 'var(--text-tertiary)' }, children: ["Signed on ", new Date(signatures[field.variableName].signedAt).toLocaleString()] }), !isDisabled && (_jsxs(Button, { variant: "ghost", onClick: () => setShowSignatureModal(field.variableName), className: "mt-2", children: [_jsx(Pen, { className: "w-4 h-4 mr-2" }), "Re-sign"] }))] })) : (_jsxs(Button, { onClick: () => setShowSignatureModal(field.variableName), disabled: isDisabled, variant: "outline", className: "w-full", style: { borderColor: '#D97706', color: '#D97706' }, children: [_jsx(Pen, { className: "w-4 h-4 mr-2" }), "Sign Here"] })) }));
            case 'image':
            case 'file':
                return (_jsxs("div", { className: "space-y-2", children: [value && (_jsxs("div", { className: "border rounded-lg p-4", style: { borderColor: 'var(--border-color)' }, children: [field.fieldType === 'image' && (_jsx("img", { src: value, alt: field.label, className: "max-h-40 mx-auto" })), _jsx("div", { className: "mt-2 text-xs", style: { color: 'var(--text-tertiary)' }, children: "File uploaded" })] })), !isDisabled && (_jsxs(Button, { variant: "outline", className: "w-full", onClick: () => {
                                const input = document.createElement('input');
                                input.type = 'file';
                                input.accept = field.fieldType === 'image' ? 'image/*' : '*/*';
                                input.onchange = (e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        const reader = new FileReader();
                                        reader.onload = (event) => {
                                            onImageUpload(field.variableName, {
                                                data: event.target?.result,
                                                fileName: file.name,
                                                mimeType: file.type
                                            });
                                        };
                                        reader.readAsDataURL(file);
                                    }
                                };
                                input.click();
                            }, children: [_jsx(Upload, { className: "w-4 h-4 mr-2" }), "Upload ", field.fieldType === 'image' ? 'Image' : 'File'] }))] }));
            default:
                return (_jsx("input", { type: "text", value: value, onChange: (e) => onChange(field.variableName, e.target.value), disabled: isDisabled, placeholder: field.placeholder, required: isRequired, className: baseInputClass, style: inputStyle }));
        }
    };
    return (_jsxs("div", { className: "space-y-6", children: [Object.entries(groupedFields).map(([groupName, groupFields]) => (_jsxs("div", { className: "space-y-4", children: [groupName !== 'general' && (_jsx("h3", { className: "text-lg font-semibold border-b pb-2", style: { color: 'var(--text-primary)', borderColor: 'var(--border-color)' }, children: groupName.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') })), _jsx("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4", children: groupFields.map((field) => (_jsx("div", { className: field.fieldType === 'address' || field.fieldType === 'signature' ? 'md:col-span-2' : '', children: _jsxs("label", { className: "block mb-2", children: [_jsxs("div", { className: "flex items-center gap-2 mb-1", children: [_jsxs("span", { className: "text-sm font-medium", style: { color: 'var(--text-secondary)' }, children: [field.label, field.validation?.required && (_jsx("span", { className: "text-red-500 ml-1", children: "*" }))] }), !canFillField(field) && (_jsx("span", { className: "text-xs px-2 py-0.5 rounded", style: { backgroundColor: 'var(--bg-elevated)', color: 'var(--text-tertiary)' }, children: "Read-only" }))] }), field.helpText && (_jsx("div", { className: "text-xs mb-2", style: { color: 'var(--text-tertiary)' }, children: field.helpText })), renderField(field)] }) }, field.variableName))) })] }, groupName))), showSignatureModal && (_jsx(SignatureModal, { fieldName: showSignatureModal, onClose: () => setShowSignatureModal(null), onSave: (signatureData) => {
                    onSignature(showSignatureModal, signatureData);
                    setShowSignatureModal(null);
                } }))] }));
}
// Signature capture modal component
function SignatureModal({ fieldName, onClose, onSave }) {
    const canvasRef = React.useRef(null);
    const [isDrawing, setIsDrawing] = React.useState(false);
    React.useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
    }, []);
    const startDrawing = (e) => {
        setIsDrawing(true);
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const rect = canvas.getBoundingClientRect();
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        ctx.beginPath();
        ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    };
    const draw = (e) => {
        if (!isDrawing)
            return;
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const rect = canvas.getBoundingClientRect();
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
        ctx.stroke();
    };
    const stopDrawing = () => {
        setIsDrawing(false);
    };
    const clearSignature = () => {
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    };
    const saveSignature = () => {
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const dataUrl = canvas.toDataURL('image/png');
        onSave({
            type: 'drawn',
            data: dataUrl,
            signedAt: new Date(),
            signedBy: 'current-user' // TODO: Get from auth context
        });
    };
    return (_jsx("div", { className: "fixed inset-0 bg-black/50 flex items-center justify-center z-50", children: _jsxs("div", { className: "rounded-2xl p-6 max-w-2xl w-full mx-4", style: { backgroundColor: 'var(--bg-elevated)' }, children: [_jsx("h3", { className: "text-xl font-semibold mb-4", style: { color: 'var(--text-primary)' }, children: "Sign Document" }), _jsx("div", { className: "mb-4", children: _jsx("canvas", { ref: canvasRef, width: 600, height: 200, onMouseDown: startDrawing, onMouseMove: draw, onMouseUp: stopDrawing, onMouseLeave: stopDrawing, className: "border rounded-lg w-full cursor-crosshair", style: { borderColor: 'var(--border-color)', backgroundColor: 'white' } }) }), _jsxs("div", { className: "flex gap-3", children: [_jsx(Button, { variant: "ghost", onClick: clearSignature, children: "Clear" }), _jsx("div", { className: "flex-1" }), _jsx(Button, { variant: "ghost", onClick: onClose, children: "Cancel" }), _jsx(Button, { onClick: saveSignature, style: { backgroundColor: '#D97706', color: 'white' }, children: "Save Signature" })] })] }) }));
}
